import { useEffect, useState } from "react";
import {
  getConsent,
  OPEN_COOKIE_SETTINGS_EVENT,
  setConsent,
} from "@/lib/cookieConsent";
import { disableTelemetry, initTelemetry } from "@/lib/telemetry";
import { disableGoogleAnalytics, initGoogleAnalytics } from "@/lib/googleAnalytics";
import { Button } from "@/components/ui/button";

/**
 * Consent banner shown until the visitor chooses. "Accept all" records consent
 * and initialises analytics (Application Insights + Google Analytics 4) there and then; "Reject
 * non-essential" records the refusal and leaves analytics off. On subsequent
 * visits the stored choice is honoured (main.tsx only inits telemetry when the
 * cookie says accepted), so the banner never reappears.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(() => getConsent() === null);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, open);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setConsent("accepted");
    void initTelemetry();
    initGoogleAnalytics();
    setVisible(false);
  };
  const reject = () => {
    disableTelemetry();
    disableGoogleAnalytics();
    setConsent("rejected");
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          We use one essential cookie to remember this choice and, only with your consent,
          analytics cookies to understand usage and improve the site. You can change your choice
          at any time. See our{" "}
          <a href="/cookies" className="text-primary hover:underline">Cookie Policy</a>.
        </p>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={reject}>
            Reject non-essential
          </Button>
          <Button size="sm" onClick={accept}>
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
